/**
 * Analytics React Hooks (#50)
 *
 * Provides convenient hooks for page view tracking and event tracking.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { trackPageView, trackEvent } from '../services/analytics';
import type { AnalyticsEventName, AnalyticsEventProperties } from '../types/api';

/**
 * Track page views on route changes.
 * Place this once in DashboardLayout or a top-level route wrapper.
 */
export function useAnalyticsPageView(): void {
  const location = useLocation();
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    // Only fire if path actually changed (not just search/hash)
    if (location.pathname !== prevPath.current) {
      prevPath.current = location.pathname;
    }
    trackPageView(location.pathname);
  }, [location.pathname]);
}

/**
 * Returns a stable track function scoped to a component.
 * Automatically attaches the current page path.
 */
export function useTrackEvent() {
  const location = useLocation();

  return (event: AnalyticsEventName, properties?: AnalyticsEventProperties) => {
    trackEvent(event, { page: location.pathname, ...properties });
  };
}
